import { PlatformType } from "../models/Platform";
import { MediaItem } from "../models/Post";

export type MediaType = "image" | "video";

type MediaRequirements = {
  maxFiles?: number;
  supportedTypes: MediaType[];
  // Platform converts image carousels to a video slideshow at publish time,
  // so all-image posts are publishable even though images aren't supported
  convertsImagesToVideo?: boolean;
};

type ValidationError = {
  code: string;
  message: string;
  mediaIndex?: number;
};

type MediaValidationResult = {
  isValid: boolean;
  errors: ValidationError[];
};

/*************************************************************
 ****************** Platform Requirements ********************
 *************************************************************/

// Only content constraints belong here. Source encoding and dimensions do not
// describe the final rendered media; API byte limits are handled on rejection.
export const PLATFORM_MEDIA_REQUIREMENTS: Record<
  PlatformType,
  MediaRequirements
> = {
  instagram: {
    maxFiles: 10,
    supportedTypes: ["image", "video"],
  },
  youtube: {
    maxFiles: 1,
    supportedTypes: ["video"],
    convertsImagesToVideo: true,
  },
  tiktok: {
    maxFiles: 35,
    supportedTypes: ["image", "video"],
  },
  linkedin: {
    maxFiles: 35,
    supportedTypes: ["video", "image"],
  },
  facebook: {
    maxFiles: 10,
    supportedTypes: ["video", "image"],
  },
  pinterest: {
    supportedTypes: ["image"],
    maxFiles: 5,
  },
};

export const platformSupportsVideoPosts = (platform: PlatformType): boolean =>
  PLATFORM_MEDIA_REQUIREMENTS[platform]?.supportedTypes.includes("video") ??
  false;

/*************************************************************
 ************************ Validators *************************
 *************************************************************/

export const validateFileCount = (
  items: MediaItem[],
  maxFiles: number,
): ValidationError | null => {
  if (items.length > maxFiles) {
    return {
      code: "MAX_FILES_EXCEEDED",
      message: `Maximum ${maxFiles} files allowed`,
    };
  }
  return null;
};

export const validateFileType = (
  item: MediaItem,
  supportedTypes: MediaType[],
  index: number,
): ValidationError | null => {
  const type = item.type as MediaType;
  if (!supportedTypes.includes(type)) {
    return {
      code: "UNSUPPORTED_TYPE",
      message: `File type ${type} is not supported`,
      mediaIndex: index,
    };
  }
  return null;
};

export const validateImageCarousel = (items: MediaItem[]) => {
  if (items.length > 1 && items.some((item) => item.type === "video")) {
    return {
      code: "UNSUPPORTED_TYPE",
      message: `Only image carousels are supported`,
    };
  }
  return null;
};

export const validateMediaForPlatform = (
  uploadItems: MediaItem[],
  platform: PlatformType,
  isVideoPost = false,
): MediaValidationResult => {
  // Video posts will be rendered as a single MP4 at publish time —
  // skip carousel/image validation only for platforms that can publish video.
  if (isVideoPost && platformSupportsVideoPosts(platform)) {
    return { isValid: true, errors: [] };
  }
  const requirements = PLATFORM_MEDIA_REQUIREMENTS[platform];
  // Image carousels are converted to a video slideshow at publish time for
  // this platform (e.g. YouTube), so the final output is also video
  if (
    requirements.convertsImagesToVideo &&
    uploadItems.length >= 1 &&
    uploadItems.every((item) => item.type === "image")
  ) {
    return { isValid: true, errors: [] };
  }
  const errors = [];

  // Check file count
  if (requirements.maxFiles !== undefined) {
    const fileCountError = validateFileCount(
      uploadItems,
      requirements.maxFiles,
    );
    if (fileCountError) errors.push(fileCountError);
  }
  const carouselError = validateImageCarousel(uploadItems);
  if (carouselError) errors.push(carouselError);
  // Check individual files
  uploadItems.forEach((item, index) => {
    // File type validation
    const typeError = validateFileType(
      item,
      requirements.supportedTypes,
      index,
    );
    if (typeError) errors.push(typeError);
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};
// Helper function to format validation errors for display
export const formatValidationErrors = (
  result: MediaValidationResult,
  platform: PlatformType,
): string[] => {
  return result.errors.map((error) => {
    const mediaPrefix =
      error.mediaIndex !== undefined ? `Media ${error.mediaIndex + 1}: ` : "";
    return `${mediaPrefix}${error.message} (${platform})`;
  });
};
